export type Point = { x: number; y: number };
export type Rect = { x: number; y: number; w: number; h: number };
export type View = { cx: number; cy: number; s: number };
export type Insets = {
    top: number;
    right: number;
    bottom: number;
    left: number;
};

export function containRect(box: Rect, aspect: number): Rect {
    const boxAspect = box.w / box.h;
    const w = aspect >= boxAspect ? box.w : box.h * aspect;
    const h = aspect >= boxAspect ? box.w / aspect : box.h;
    return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

export function viewOfRect(rect: Rect, fit: Rect): View {
    return {
        cx: rect.x + rect.w / 2,
        cy: rect.y + rect.h / 2,
        s: rect.w / fit.w,
    };
}

export function fitView(fit: Rect): View {
    return { cx: fit.x + fit.w / 2, cy: fit.y + fit.h / 2, s: 1 };
}

export function centroid(points: readonly Point[]): Point {
    const [a, b] = points;
    if (!a) return { x: 0, y: 0 };
    if (!b) return a;
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function spread(points: readonly Point[]): number {
    const [a, b] = points;
    if (!a || !b) return 0;
    return Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
}

export function zoomAbout(view: View, anchor: Point, s: number): View {
    const k = s / view.s;
    return {
        cx: anchor.x + (view.cx - anchor.x) * k,
        cy: anchor.y + (view.cy - anchor.y) * k,
        s,
    };
}

export function pinchView(
    start: View,
    startCentroid: Point,
    s: number,
    current: Point,
): View {
    const k = s / start.s;
    return {
        cx: current.x + (start.cx - startCentroid.x) * k,
        cy: current.y + (start.cy - startCentroid.y) * k,
        s,
    };
}

type Bounds = { minX: number; maxX: number; minY: number; maxY: number };

export function panBounds(fit: Rect, area: Rect, s: number): Bounds {
    const w = fit.w * s;
    const h = fit.h * s;
    const midX = area.x + area.w / 2;
    const midY = area.y + area.h / 2;
    const slackX = Math.max(0, (w - area.w) / 2);
    const slackY = Math.max(0, (h - area.h) / 2);
    return {
        minX: midX - slackX,
        maxX: midX + slackX,
        minY: midY - slackY,
        maxY: midY + slackY,
    };
}

export function clampView(view: View, fit: Rect, area: Rect): View {
    const b = panBounds(fit, area, view.s);
    return {
        cx: Math.min(b.maxX, Math.max(b.minX, view.cx)),
        cy: Math.min(b.maxY, Math.max(b.minY, view.cy)),
        s: view.s,
    };
}

export function rubberBand(
    value: number,
    min: number,
    max: number,
    dimension: number,
): number {
    const over = (distance: number) =>
        (1 - 1 / ((distance * 0.55) / dimension + 1)) * dimension;
    if (value < min) return min - over(min - value);
    if (value > max) return max + over(value - max);
    return value;
}

export function rubberView(view: View, fit: Rect, area: Rect): View {
    const b = panBounds(fit, area, view.s);
    return {
        cx: rubberBand(view.cx, b.minX, b.maxX, area.w),
        cy: rubberBand(view.cy, b.minY, b.maxY, area.h),
        s: view.s,
    };
}

export function settledScale(s: number, maxZoom: number): number {
    if (s < 1.08) return 1;
    return Math.min(s, maxZoom);
}

export function transformFor(view: View, fit: Rect): string {
    const tx = view.cx - (view.s * fit.w) / 2 - fit.x;
    const ty = view.cy - (view.s * fit.h) / 2 - fit.y;
    return `translate3d(${tx}px, ${ty}px, 0) scale(${view.s})`;
}

export function hiddenInsets(rect: Rect, visible: Rect): Insets {
    const part = (outside: number, size: number) =>
        Math.min(0.5, Math.max(0, outside) / size);
    return {
        top: part(visible.y - rect.y, rect.h),
        right: part(rect.x + rect.w - (visible.x + visible.w), rect.w),
        bottom: part(rect.y + rect.h - (visible.y + visible.h), rect.h),
        left: part(visible.x - rect.x, rect.w),
    };
}

export function insetClip(insets: Insets, amount: number): string {
    const pct = (side: number) => `${side * amount * 100}%`;
    return `inset(${pct(insets.top)} ${pct(insets.right)} ${pct(insets.bottom)} ${pct(insets.left)})`;
}

export function uncoveredWindow(
    rect: Rect,
    covers: readonly Rect[],
    viewportHeight: number,
): Rect {
    const midX = rect.x + rect.w / 2;
    let top = Math.max(rect.y, 0);
    let bottom = Math.min(rect.y + rect.h, viewportHeight);
    for (const c of covers) {
        const spansMid = c.x <= midX && c.x + c.w >= midX;
        if (!spansMid || c.y + c.h <= top || c.y >= bottom) continue;
        if (c.y <= top) top = Math.min(bottom, c.y + c.h);
        else bottom = c.y;
    }
    return { x: rect.x, y: top, w: rect.w, h: bottom - top };
}
